import { useQueryClient } from "@tanstack/react-query";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { listPatientChartNormalized } from "services/patient-chart/listPatientChart";
import fetchAllPages from "utils/fetch-all-pages";

export const ClientsContext = createContext<{
  individuals: PatientChartResponseEntry[];
  couples: PatientChartResponseEntry[];
  isLoadingClients: boolean;
  initializeClients: () => void;
  refreshClients: () => Promise<void>;
}>({
  individuals: [],
  couples: [],
  isLoadingClients: false,
  initializeClients: () => undefined,
  refreshClients: () => Promise.resolve(),
});

type Props = { children: ReactNode };

export const ClientsContextProvider = ({ children }: Props) => {
  const queryClient = useQueryClient();

  const isInitialized = useRef(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [individuals, setIndividuals] = useState<PatientChartResponseEntry[]>(
    [],
  );
  const [couples, setCouples] = useState<PatientChartResponseEntry[]>([]);

  const refreshClients = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: ["patientChart"],
    });

    if (!isInitialized.current) {
      // It may happen that the function is called by PatientCharts or another component which does not rely on data provided by the context
      // If no component actually consumes data from the context (yet), there is actually no stale data to refresh - the current data will be loaded by initializeClients
      return;
    }

    setIsLoadingClients(true);

    const individuals = await fetchAllPages(listPatientChartNormalized, {
      search: "",
      patientType: "individual",
      // clientStatus: ["patient"],
    });
    setIndividuals(individuals);

    const couples = await fetchAllPages(listPatientChartNormalized, {
      search: "",
      patientType: "couple",
      // clientStatus: ["patient"],
    });
    setCouples(couples);

    setIsLoadingClients(false);
  }, [queryClient]);

  const initializeClients = useCallback(() => {
    if (isInitialized.current) {
      return;
    }

    isInitialized.current = true;
    refreshClients();
  }, [refreshClients]);

  return (
    <ClientsContext.Provider
      value={{
        individuals,
        couples,
        isLoadingClients,
        initializeClients,
        refreshClients,
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
};

export const useClientsContext = () => useContext(ClientsContext);
